import { useParams } from '@tanstack/react-router';

import type {
  UsersUserElasticSchemaWithId,
  UsersUserSchema,
} from '@iconik-io/iconik-api/src/services/users';

import type { AuthUserData } from '../../ducks/auth/types';
import { useAppSelector } from '../../store/react_redux_typed';
import type { AppState } from '../../store/types';
import type { PermissionValues } from '../../types/common';

export const getActingUserId = (state: AppState): string => {
  const actAsUserId = localStorage.getItem('actAsUserId');
  return actAsUserId ?? state.authReducer.userData.id;
};

/**
 * The user page is shared by `/admin/users/$id` and `/user/profile`, which has
 * no id and shows the current user instead.
 */
export const useOpenedUserId = (): string => {
  const { id } = useParams({ strict: false });
  const currentUserId = useAppSelector(
    (state) => state.authReducer.userData.id,
  );
  return id ?? currentUserId;
};

export const getOriginalUser = (
  state: AppState,
): UsersUserSchema | undefined =>
  state.authReducer.originalUserId
    ? state.entities.users[state.authReducer.originalUserId]
    : undefined;

export const userProfilePagePermissions: PermissionValues = {
  read: true,
  write: true,
  delete: false,
  'change-acl': false,
};

export const shouldShowSwitchToUserButton = (
  currentUser: AuthUserData,
  user: UsersUserElasticSchemaWithId,
  canActAsUser: boolean,
) => {
  const isAdminUserItem = user.is_admin || user.is_super_admin;
  const isNonAdminPowerCurrentUser =
    currentUser.type === 'POWER' &&
    !(currentUser.is_admin || currentUser.is_super_admin);
  const canSwitchToUser = !(isAdminUserItem && isNonAdminPowerCurrentUser);
  const isCurrentUserItem = currentUser.id === user.id;
  return canActAsUser && !isCurrentUserItem && canSwitchToUser;
};
